import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { formatTimeBasedOnTimezone } from '../../core/utils/date-time.util';
import { RouterModule } from '@angular/router';
import { SharedModule } from '../../shared/shared.module';
import { MentorPanelService } from '../../core/services/mentor-panel/mentor-panel.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import {
    MentorBookingItem,
    MentorDashboardStatsDataResponse,
    UpcomingSessionItem,
    MentorStat,
    MentorUpcomingSession,
    MentorSessionRequest,
    SessionStatus,
} from '../../core/models/mentor-panel/mentor-panel-api-models';
import { StatusBadgeComponent } from '../../shared/status-badge/status-badge.component';


@Component({
    selector: 'app-mentor-dashboard',
    standalone: true,
    imports: [CommonModule, SharedModule, RouterModule, StatusBadgeComponent],
    templateUrl: './mentor-dashboard.component.html',
    styleUrl: './mentor-dashboard.component.scss'
})
export class MentorDashboardComponent implements OnInit {
    private mentorPanelService = inject(MentorPanelService);
    private toastr = inject(ToastrService);
    private dialog = inject(MatDialog);
    userName: string = 'Mentor';
    readonly SessionStatus = SessionStatus;

    stats: MentorStat[] = [];

    sessions: MentorUpcomingSession[] = [];

    requests: MentorSessionRequest[] = [];

    searchQuery: string = '';
    activeFilter: string = 'All';
    isLoadingStats: boolean = true;
    isLoadingSessions: boolean = true;
    isLoadingRequests: boolean = true;

    constructor() { }

    get filteredRequests(): MentorSessionRequest[] {
        if (!this.searchQuery) {
            return this.requests;
        }
        const query = this.searchQuery.toLowerCase().trim();
        return this.requests.filter(request =>
            (request.studentName || '').toLowerCase().includes(query) ||
            (request.topic || '').toLowerCase().includes(query)
        );
    }

    get filteredSessions(): MentorUpcomingSession[] {
        if (!this.searchQuery) {
            return this.sessions;
        }
        const query = this.searchQuery.toLowerCase().trim();
        return this.sessions.filter(session =>
            (session.studentName || '').toLowerCase().includes(query) ||
            (session.topic || '').toLowerCase().includes(query)
        );
    }

    ngOnInit(): void {
        this.fetchDashboardStats();
        this.fetchUpcomingSessions();
        this.fetchMentorBookings();
    }

    private fetchDashboardStats(): void {
        this.mentorPanelService.getDashboardStats().subscribe({
            next: (response) => {
                if (response.Success) {
                    this.updateStatsCards(response.Data);
                }
                this.isLoadingStats = false;
            },
            error: (err) => {
                console.error('Error fetching dashboard stats', err);
                this.isLoadingStats = false;
            }
        });
    }

    private updateStatsCards(data: MentorDashboardStatsDataResponse): void {
        const rating = data.average_rating ? Number(data.average_rating) : 0;
        this.stats = [
            { label: 'Pending', count: data.pending_requests, icon: 'bi-clock', colorClass: 'pending' },
            { label: 'Upcoming', count: data.upcoming_sessions, icon: 'bi-calendar-event', colorClass: 'upcoming' },
            { label: 'Completed', count: data.completed_sessions, icon: 'bi-check-circle', colorClass: 'completed' },
            { label: 'Total', count: data.total_sessions, icon: 'bi-bar-chart', colorClass: 'total' },
            { label: 'Avg. Rating', count: !isNaN(rating) ? rating.toFixed(1) : '0.0', icon: 'bi-star', colorClass: 'rating' }
        ];
    }

    private fetchUpcomingSessions(): void {
        this.mentorPanelService.getUpcomingSessions().subscribe({
            next: (response) => {
                if (response.Success) {
                    this.sessions = response.Data.sessions.map(s => this.mapToSession(s));
                }
                this.isLoadingSessions = false;
            },
            error: (err) => {
                console.error('Error fetching upcoming sessions', err);
                this.isLoadingSessions = false;
            }
        });
    }

    private fetchMentorBookings(): void {
        this.mentorPanelService.getMentorBookings().subscribe({
            next: (response) => {
                if (response.Success) {
                    this.requests = response.Data.bookings.map(b => this.mapToRequest(b));
                }
                this.isLoadingRequests = false;
            },
            error: (err) => {
                console.error('Error fetching mentor bookings', err);
                this.isLoadingRequests = false;
            }
        });
    }

    private mapToSession(item: UpcomingSessionItem): MentorUpcomingSession {
        return {
            id: item.booking_id,
            studentName: item.student_name,
            initials: this.getInitials(item.student_name),
            sessionType: item.session_type,
            topic: 'Session topic', // API UpcomingSessionItem doesn't have topic yet
            date: item.date,
            time: this.formatTimeTo12h(item.start_time_utc),
            duration: `${item.duration_minutes} min`,
            isToday: this.isToday(item.date),
            status: this.isToday(item.date) ? 'TODAY' : 'ACCEPTED',
            type: 'PAID',
            avatarColorClass: this.getRandomAvatarColor(item.student_name),
            googleMeetLink: item.google_meet_link
        };
    }

    private mapToRequest(item: MentorBookingItem): MentorSessionRequest {
        return {
            id: item.booking_id,
            studentName: item.student_name,
            initials: this.getInitials(item.student_name),
            date: item.appointment_date,
            time: this.formatTimeTo12h(item.start_time),
            duration: `${item.duration_minutes} min`,
            topic: item.topics,
            status: item.status,
            category: item.is_free ? 'FREE' : 'PAID',
            avatarColorClass: this.getRandomAvatarColor(item.student_name)
        };
    }

    private getInitials(name: string): string {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    }

    private getRandomAvatarColor(name: string): string {
        const colors = ['blue-avatar', 'purple-avatar', 'orange-avatar', 'green-avatar'];
        const index = name.length % colors.length;
        return colors[index];
    }

    private formatTimeTo12h(isoString: string): string {
        return formatTimeBasedOnTimezone(isoString);
    }

    private isToday(dateStr: string): boolean {
        const today = new Date().toISOString().split('T')[0];
        return dateStr === today;
    }

    joinMeeting(session: MentorUpcomingSession) {
        if (session.googleMeetLink) {
            window.open(session.googleMeetLink, '_blank');
        } else {
            this.toastr.info('No meeting link available for this session.');
        }
    }

    markComplete(session: MentorUpcomingSession) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Mark as Completed',
                message: `Are you sure you want to mark the session with ${session.studentName} as completed?`,
                confirmText: 'Complete',
                cancelText: 'Cancel',
                type: 'submit'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.mentorPanelService.markBookingAsDone(session.id).subscribe({
                    next: (response) => {
                        if (response.Success) {
                            this.toastr.success(response.Message || 'Session marked as completed successfully');
                            this.fetchUpcomingSessions();
                            this.fetchDashboardStats();
                        } else {
                            this.toastr.error(response.Message || 'Failed to mark session as complete');
                        }
                    },
                    error: (err) => {
                        this.toastr.error('An error occurred while marking the session as complete');
                        console.error('Error marking session as done', err);
                    }
                });
            }
        });
    }

    acceptRequest(request: MentorSessionRequest) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Accept Session Request',
                message: `Are you sure you want to accept the session request from ${request.studentName}?`,
                confirmText: 'Accept',
                cancelText: 'Cancel',
                type: 'submit'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.mentorPanelService.updateBookingStatus(request.id, { action: true }).subscribe({
                    next: (response) => {
                        if (response.Success) {
                            this.toastr.success(response.Message || 'Session request accepted successfully');
                            this.fetchMentorBookings();
                            this.fetchUpcomingSessions();
                            this.fetchDashboardStats();
                        }
                    }
                });
            }
        });
    }

    rejectRequest(request: MentorSessionRequest) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Reject Session Request',
                message: `Are you sure you want to reject the session request from ${request.studentName}? This action cannot be undone.`,
                confirmText: 'Reject',
                cancelText: 'Cancel',
                type: 'quit'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.mentorPanelService.updateBookingStatus(request.id, { action: false }).subscribe({
                    next: (response) => {
                        if (response.Success) {
                            this.toastr.info(response.Message || 'Session request rejected');
                            this.fetchMentorBookings();
                            this.fetchDashboardStats();
                        } else {
                            this.toastr.error(response.Message || 'Failed to reject session request');
                        }
                    },
                    error: (err) => {
                        this.toastr.error('An error occurred while rejecting the request');
                        console.error('Error rejecting request', err);
                    }
                });
            }
        });
    }


    setFilter(filter: string) {
        this.activeFilter = filter;
    }
}
